import * as yup from 'yup';

import {
	HAGGLE_ACCEPTABLE_MARGIN_MAX,
	HAGGLE_ACCEPTABLE_MARGIN_MIN,
	HAGGLE_ACCEPTABLE_MARGIN_REQUIRED,
	HAGGLE_ADD_AUTO_SHOPIFY_POLICY_REQUIRED,
	HAGGLE_B2B_INVITED_EMAILS_REQUIRED,
	HAGGLE_B2B_PASSWORD_REQUIRED,
	HAGGLE_CHECKOUT_TIME_MAX,
	HAGGLE_CHECKOUT_TIME_MIN,
	HAGGLE_CUSTOM_RETURN_AND_EXCHANGE_POLICY_REQUIRED,
	HAGGLE_END_DATETIME_AFTER_START_DATETIME,
	HAGGLE_END_DATETIME_REQUIRED,
	HAGGLE_FOLLOW_UP_OFFER_VALIDITY_AFTER_END_TIME,
	HAGGLE_FREE_SHIPPING_THRESHOLD_MAX,
	HAGGLE_FREE_SHIPPING_THRESHOLD_MIN,
	HAGGLE_HOST_HAGGLE_EVENT_ON_SHOPIFY_REQUIRED,
	HAGGLE_MIN_ORDER_QUANTITY_MIN,
	HAGGLE_MIN_ORDER_VALUE_MIN,
	HAGGLE_PRODUCT_IDS_MAX,
	HAGGLE_PRODUCT_IDS_REQUIRED,
	HAGGLE_SHOW_BANNER_REQUIRED,
	HAGGLE_START_DATETIME_REQUIRED,
	HAGGLE_STRIPE_CONNECT_REQUIRED,
	HAGGLE_UPCOMING_PREVIEW_DATE_AFTER_CURRENT_TIME,
	HAGGLE_UPCOMING_PREVIEW_DATE_BEFORE_START_DATETIME,
} from './haggle-event-schema.errors';

import { MAX_PRODUCTS_SELECTION } from '@/components/utils/constants';
import { HaggleType } from '@/components/utils/enums';

export const createHaggleEventSchema = (isStripeConnected: boolean) => {
	return yup.object({
		acceptableMargin: yup
			.number()
			.min(1, HAGGLE_ACCEPTABLE_MARGIN_MIN)
			.max(100, HAGGLE_ACCEPTABLE_MARGIN_MAX)
			.required(HAGGLE_ACCEPTABLE_MARGIN_REQUIRED),
		startDateTime: yup.date().required(HAGGLE_START_DATETIME_REQUIRED),
		endDateTime: yup
			.date()
			.required(HAGGLE_END_DATETIME_REQUIRED)
			.test(
				'is-after-start',
				HAGGLE_END_DATETIME_AFTER_START_DATETIME,
				function (value) {
					const { startDateTime } = this.parent;
					if (!startDateTime || !value) return true;
					return new Date(value) > new Date(startDateTime);
				},
			),
		productIds: yup
			.array()
			.min(1, HAGGLE_PRODUCT_IDS_REQUIRED)
			.max(MAX_PRODUCTS_SELECTION, HAGGLE_PRODUCT_IDS_MAX)
			.required(HAGGLE_PRODUCT_IDS_REQUIRED),
		limitCheckoutTime: yup
			.number()
			.min(5, HAGGLE_CHECKOUT_TIME_MIN)
			.max(60, HAGGLE_CHECKOUT_TIME_MAX)
			.optional(),
		freeShippingThreshold: yup
			.number()
			.nullable()
			.min(10, HAGGLE_FREE_SHIPPING_THRESHOLD_MIN)
			.max(100000, HAGGLE_FREE_SHIPPING_THRESHOLD_MAX)
			.optional(),
		followUpOfferValidity: yup
			.date()
			.optional()
			.test(
				'after-end-datetime',
				HAGGLE_FOLLOW_UP_OFFER_VALIDITY_AFTER_END_TIME,
				function (value) {
					if (!value) return true;
					const { endDateTime } = this.parent;
					if (!endDateTime) return true;
					return new Date(value) > new Date(endDateTime);
				},
			),
		showHaggleBannerOnShopify: yup
			.string()
			.oneOf(['true', 'false'])
			.required(HAGGLE_SHOW_BANNER_REQUIRED),
		addAutoShopifyPolicy: yup
			.string()
			.oneOf(['true', 'false', 'csv'])
			.required(HAGGLE_ADD_AUTO_SHOPIFY_POLICY_REQUIRED),
		customReturnAndExchangePolicy: yup
			.string()
			.when('addAutoShopifyPolicy', (value, schema) => {
				const policyValue = Array.isArray(value) ? value[0] : value;
				return policyValue === 'false'
					? schema.required(HAGGLE_CUSTOM_RETURN_AND_EXCHANGE_POLICY_REQUIRED)
					: schema.notRequired();
			}),
		upcomingPreviewDate: yup
			.date()
			.optional()
			.test(
				'after-current-time',
				HAGGLE_UPCOMING_PREVIEW_DATE_AFTER_CURRENT_TIME,
				function (value) {
					if (!value) return true;
					return new Date(value) > new Date();
				},
			)
			.test(
				'before-start-datetime',
				HAGGLE_UPCOMING_PREVIEW_DATE_BEFORE_START_DATETIME,
				function (value) {
					if (!value) return true;
					const { startDateTime } = this.parent;
					if (!startDateTime) return true;
					return new Date(value) < new Date(startDateTime);
				},
			),
		hostHaggleEventOnShopify: yup
			.string()
			.oneOf(['true', 'false'])
			.required(HAGGLE_HOST_HAGGLE_EVENT_ON_SHOPIFY_REQUIRED)
			.test(
				'is-stripe-connected-check',
				HAGGLE_STRIPE_CONNECT_REQUIRED,
				function (value) {
					if (value === 'true') return true;
					return isStripeConnected === true;
				},
			),
		minOrderValue: yup
			.number()
			.min(1, HAGGLE_MIN_ORDER_VALUE_MIN)
			.nullable()
			.optional(),
		minOrderQuantity: yup
			.number()
			.min(1, HAGGLE_MIN_ORDER_QUANTITY_MIN)
			.nullable()
			.optional(),
		password: yup.string().when('haggleType', {
			is: (value: string) =>
				value === HaggleType.SEMI_PRIVATE || value === HaggleType.PRIVATE,
			then: (schema) => schema.required(HAGGLE_B2B_PASSWORD_REQUIRED),
			otherwise: (schema) => schema.nullable().optional(),
		}),
		invitedEmails: yup
			.array()
			.of(yup.string())
			.when('haggleType', {
				is: HaggleType.PRIVATE,
				then: (schema) => schema.min(1, HAGGLE_B2B_INVITED_EMAILS_REQUIRED),
				otherwise: (schema) => schema.nullable().optional(),
			}),
	});
};
